/**
 * CategoryDetailCard — one card per category with:
 *   • Category label + selected-month spend
 *   • Editable budget for the selected month
 *   • Variance vs budget (over / under, colour-coded)
 *   • Miniature bar chart of monthly spend (identical style to the main chart,
 *     scoped to this single category) with a horizontal budget reference line
 *
 * The chart is horizontally scrollable so labels stay readable when many
 * months accumulate.
 */
import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LabelList, Cell,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import { Check, Pencil, X } from "lucide-react";
import { money, setBudget } from "@/lib/operations/api";
import { toast } from "sonner";

const BAR_COLOR       = "#0f2c4f";
const BUDGET_LINE     = "#e11d48";

const MONTH_SLOT_PX = 68;
const CHART_HEIGHT  = 180;

// "YYYY-MM" → "Jun 26"
function shortMonth(ym) {
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-US", { month: "short" }) + " " + y.slice(2);
}

function moneyCompact(v) {
  const n = Number(v) || 0;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `$${(n / 1_000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
}

export default function CategoryDetailCard({
  category,      // { key, label }
  Icon,
  months,        // array of "YYYY-MM"
  spendByMonth,  // { "YYYY-MM": amount }
  budgetByMonth, // { "YYYY-MM": amount }
  selectedMonth, // "all" | "YYYY-MM"
  costCentre,
  onBudgetSaved,
  onBarClick,    // (monthKey) => void
}) {
  const scopedMonth = selectedMonth === "all" ? null : selectedMonth;

  const spendForScope = scopedMonth
    ? Number(spendByMonth[scopedMonth] || 0)
    : Object.values(spendByMonth).reduce((s, v) => s + Number(v || 0), 0);

  const budgetForScope = scopedMonth
    ? Number(budgetByMonth[scopedMonth] || 0)
    : Object.values(budgetByMonth).reduce((s, v) => s + Number(v || 0), 0);

  const variance      = spendForScope - budgetForScope;
  const hasBudget     = budgetForScope > 0;
  const overBudget    = hasBudget && variance > 0;

  const data = months.map((m) => ({
    month: shortMonth(m),
    key: m,
    amount: Number(spendByMonth[m] || 0),
    budget: Number(budgetByMonth[m] || 0),
  }));

  // Reference line = budget for the selected month (or average across the
  // dataset if "All time" is selected). Rendered only when a value exists.
  const refBudget = scopedMonth
    ? budgetForScope
    : (Object.values(budgetByMonth).length > 0
        ? Object.values(budgetByMonth).reduce((s, v) => s + Number(v), 0) /
          Object.values(budgetByMonth).length
        : 0);

  const chartWidth = Math.max(360, months.length * MONTH_SLOT_PX + 80);

  return (
    <div
      className="rounded-2xl bg-white border border-slate-200"
      data-testid={`ops-cat-detail-${category.key}`}
    >
      {/* Header row: label + amounts + budget editor */}
      <div className="px-4 sm:px-5 pt-4 pb-3 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-brand-navy shrink-0">
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                {category.label}
              </p>
              <p className="text-lg font-bold text-brand-navy tabular-nums truncate">
                {money(spendForScope)}
                <span className="text-xs text-slate-400 font-medium ml-2">
                  {scopedMonth ? "spent this month" : "spent all-time"}
                </span>
              </p>
            </div>
          </div>

          {/* Budget cell */}
          <BudgetCell
            costCentre={costCentre}
            categoryKey={category.key}
            scopedMonth={scopedMonth}
            currentBudget={budgetForScope}
            onSaved={onBudgetSaved}
          />
        </div>

        {/* Variance pill */}
        <div className="mt-3 flex items-center gap-2 text-xs tabular-nums">
          {hasBudget ? (
            <>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${
                overBudget
                  ? "bg-rose-50 text-rose-700 border border-rose-200"
                  : "bg-emerald-50 text-emerald-700 border border-emerald-200"
              }`}>
                {overBudget ? "Over" : "Under"} by {money(Math.abs(variance))}
              </span>
              <span className="text-slate-400">
                {((spendForScope / budgetForScope) * 100 || 0).toFixed(0)}% of budget
              </span>
            </>
          ) : (
            <span className="text-slate-400">
              No budget set{scopedMonth ? " for this month" : ""}
            </span>
          )}
        </div>
      </div>

      {/* Mini chart — horizontally scrollable */}
      <div className="p-3 sm:p-4">
        <div className="overflow-x-auto -mx-1">
          <div style={{ width: chartWidth, height: CHART_HEIGHT }} className="px-1">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                margin={{ top: 20, right: 12, left: 0, bottom: 4 }}
              >
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 10, fill: "#334155", fontWeight: 600 }}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickLine={false}
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={moneyCompact}
                  width={48}
                />
                <Tooltip
                  cursor={{ fill: "rgba(15,44,79,0.04)" }}
                  contentStyle={{
                    borderRadius: 6,
                    border: "1px solid #e2e8f0",
                    fontSize: 12,
                    padding: "6px 10px",
                  }}
                  formatter={(v, name) => [money(Number(v)), name === "budget" ? "Budget" : category.label]}
                  labelStyle={{ color: "#0f172a", fontWeight: 700 }}
                />
                {refBudget > 0 && (
                  <ReferenceLine
                    y={refBudget}
                    stroke={BUDGET_LINE}
                    strokeDasharray="4 3"
                    ifOverflow="extendDomain"
                    label={{
                      value: `Budget ${moneyCompact(refBudget)}`,
                      position: "insideTopRight",
                      fill: BUDGET_LINE,
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  />
                )}
                <Bar
                  dataKey="amount"
                  radius={[3, 3, 0, 0]}
                  maxBarSize={30}
                  cursor="pointer"
                  onClick={(d) => onBarClick?.(d?.key)}
                >
                  {data.map((d) => (
                    <Cell key={d.key} fill={BAR_COLOR} />
                  ))}
                  <LabelList
                    dataKey="amount"
                    position="top"
                    formatter={(v) => (v > 0 ? moneyCompact(v) : "")}
                    style={{ fill: "#0f2c4f", fontSize: 10, fontWeight: 700 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function BudgetCell({ costCentre, categoryKey, scopedMonth, currentBudget, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(String(currentBudget || ""));
  const [saving, setSaving]   = useState(false);

  const canEdit = !!scopedMonth; // budgets are keyed by month

  const commit = async () => {
    const amt = Math.max(0, Number(draft) || 0);
    setSaving(true);
    try {
      await setBudget({
        cost_centre: costCentre,
        category: categoryKey,
        month: scopedMonth,
        amount: amt,
      });
      toast.success("Budget saved");
      onSaved?.();
      setEditing(false);
    } catch {
      toast.error("Could not save budget");
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="text-right">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
          Budget
        </p>
        <p className="text-sm font-bold text-slate-700 tabular-nums">
          {currentBudget > 0 ? money(currentBudget) : "—"}
        </p>
        <p className="text-[10px] text-slate-400">Select a month to edit</p>
      </div>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => { setDraft(String(currentBudget || "")); setEditing(true); }}
        className="text-right group"
        data-testid={`ops-budget-edit-${categoryKey}`}
      >
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
          Budget
        </p>
        <p className="text-sm font-bold text-slate-700 tabular-nums flex items-center justify-end gap-1.5">
          {currentBudget > 0 ? money(currentBudget) : "Set budget"}
          <Pencil className="h-3.5 w-3.5 text-slate-400 group-hover:text-brand-navy" />
        </p>
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <div className="relative">
        <span className="absolute inset-y-0 left-2 flex items-center text-slate-400 text-xs font-semibold">$</span>
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setEditing(false);
          }}
          className="h-9 w-28 pl-5 pr-2 text-sm font-semibold tabular-nums text-brand-navy rounded-md border border-slate-300 focus:border-brand-navy focus:ring-1 focus:ring-brand-navy outline-none"
          autoFocus
          data-testid={`ops-budget-input-${categoryKey}`}
        />
      </div>
      <button
        type="button"
        onClick={commit}
        disabled={saving}
        className="h-9 w-9 rounded-md bg-brand-navy text-white hover:bg-brand-navy/90 disabled:opacity-50 flex items-center justify-center"
        data-testid={`ops-budget-save-${categoryKey}`}
        aria-label="Save budget"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => setEditing(false)}
        className="h-9 w-9 rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center"
        aria-label="Cancel"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
